import type { FC } from 'react';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { getEmojiValueFromContent } from '@confluence/emoji-title/entry-points/transformer.ts';
import { useElementSeenCallback } from '@confluence/analytics/entry-points/useElementSeenCallback.ts';
import { LivePagePreloader } from '@confluence/live-page-preloader/entry-points/LivePagePreloader.ts';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';

import { getContentLink, getSpaceLink, getUserAccountId } from '../feedCard-helpers.ts';
import { Wrapper } from '../FeedCard/FeedCard.tsx';
import { FeedCardBody } from '../FeedCard/FeedCardBody.tsx';
import { FeedCardInvisibleFullSizedLink } from '../FeedCard/FeedCardInvisibleFullSizedLink.tsx';
import { FeedCardContext } from '../FeedCard/FeedCardContext.ts';
import { useFeedCardAnalyticsEvents } from '../useFeedCardAnalyticsEvents.ts';
import { useIsMounted } from '../useIsMounted.ts';
import type { FilterByLabelFeedQuery$data } from '../../__generated__/FilterByLabelFeedQuery.graphql.ts';
import type { PopularFeedTimeGranularityValue } from '../../Feed.tsx';
import type { PopularFeedItem, PopularFeedItemV2 } from '../../popularFeedTypes.ts';

const avatarWrapperStyles = xcss({
	paddingRight: 'space.200',
});

// Extract the search result type from the generated query data
type SearchResult = NonNullable<NonNullable<FilterByLabelFeedQuery$data['search']>['nodes']>[0];

// Note: `PopularFeedItemV2` is structurally a subtype of `PopularFeedItem`
// (it adds an optional `aiProperty` field), so TypeScript collapses the union
// to `PopularFeedItem | SearchResult`. The V2 type is still used at the
// `feedItem as PopularFeedItemV2` cast site below to access the V2-only field.
type FeedNodeType = PopularFeedItem | SearchResult;

type FeedCardProps = {
	feedItem: FeedNodeType;
	ranking: number;
	timeGranularity?: PopularFeedTimeGranularityValue;
	disableAnimation?: boolean;
};

export const PopularFeedCard: FC<FeedCardProps> = memo(
	({ feedItem, ranking, disableAnimation, timeGranularity }) => {
		const isMounted = useIsMounted();
		const [isActionMenuOpen] = useState(false);
		const bottomOfCardRef = useRef<HTMLDivElement | null>(null);
		const [, setHasBeenHovered] = useState(false);

		const authorName = feedItem?.content?.history?.createdBy?.displayName || '';
		const authorId = getUserAccountId(feedItem?.content?.history?.createdBy) || '';
		const authorSrc = feedItem?.content?.history?.createdBy?.profilePicture?.path || '';
		const ownerName = feedItem?.content?.history?.ownedBy?.displayName || '';
		const ownerId = getUserAccountId(feedItem?.content?.history?.ownedBy) || '';
		const ownerSrc = feedItem?.content?.history?.ownedBy?.profilePicture?.path || '';
		const contentUrl = getContentLink(feedItem?.content);
		const contentId = feedItem?.content?.id || '';
		const contentType = feedItem?.content?.type || 'page';
		const contentSubType = feedItem?.content?.subType;
		const contentTitle = feedItem?.content?.title || '';
		const contentExcerpt = feedItem?.content?.excerpt;
		const creationDate = feedItem?.content?.history?.createdDate || '';
		const friendlyWhen = feedItem?.content?.version?.friendlyWhen || '';
		const spaceName = feedItem?.content?.space?.name || '';
		const spaceId = feedItem?.content?.space?.id || '';
		const spaceUrl = getSpaceLink(feedItem?.content);
		const numComments = feedItem?.content?.metadata?.comments?.commentsCount || 0;
		const emoji = getEmojiValueFromContent(feedItem?.content);
		const isLivePage = contentType === 'page' && contentSubType === 'live';
		// V2 path: aiProperty is resolved directly on PopularFeedItem (parallel with content hop)
		// Narrow via 'in' check rather than type assertion (avoids no-unnecessary-type-assertion).
		const aiSnippetV2 =
			feedItem && 'aiProperty' in feedItem
				? (feedItem as PopularFeedItemV2).aiProperty?.objectData
				: undefined;
		// V1 path: aiProperty is nested under content (serial — waits for Confluence hop)
		// Narrow via 'in' check; TS infers aiProperty's type from the narrowing.
		const v1Content = feedItem?.content;
		const aiSnippetV1 =
			v1Content && 'aiProperty' in v1Content ? v1Content.aiProperty?.objectData : undefined;

		// Use the NO-exposure gate variants here. PopularFeedCard renders many
		// times per scroll (one per visible card), so a non-suppressing gate
		// would over-fire exposures and skew experiment cohort/arm sizes.
		// Exposure for each gate is already logged exactly once per render at
		// the point of decision in the hook above:
		//   - `confluence_ai_snippets_popular_feed` fires in V2's `skipQuery`
		//     via `expValEquals(...)` (see usePopularFeedWithTimeGranularityStorage).
		//   - `confluence_ai_snippets_in_following_popular_feeds` fires in
		//     the V1-with-snippets path via the experiment SDK upstream.
		const hasAiSnippetV2 =
			aiSnippetV2 &&
			UNSAFE_expValEqualsNoExposure('confluence_ai_snippets_popular_feed', 'isEnabled', true);

		const hasAiSnippet =
			hasAiSnippetV2 ||
			(aiSnippetV1 &&
				UNSAFE_expValEqualsNoExposure(
					'confluence_ai_snippets_in_following_popular_feeds',
					'isEnabled',
					true,
				));

		const aiSnippet = hasAiSnippetV2 ? aiSnippetV2 : aiSnippetV1;

		// `aiSnippet` can be `null` when the GraphQL field resolved to `null`.
		// `contentExcerpt` prop is typed as `string | undefined`, so coerce nulls.
		const summaryContent = (hasAiSnippet ? aiSnippet : contentExcerpt) ?? undefined;

		const handleMouseEnter = React.useCallback(() => {
			setHasBeenHovered(true);
		}, [setHasBeenHovered]);

		const fireAnalyticsEvent = useFeedCardAnalyticsEvents(contentId, spaceId, 'popular', {
			ranking,
			contentType,
			creationDate: new Date(creationDate).getTime(),
			commentsCount: numComments,
			popularFeedTimeGranularity: timeGranularity,
		});

		const onObserved = useCallback(() => {
			fireAnalyticsEvent({
				type: 'sendTrackEvent',
				action: 'observed',
				actionSubject: 'popular-feedCard',
			});
		}, [fireAnalyticsEvent]);

		useElementSeenCallback(bottomOfCardRef, onObserved);

		const feedCardContextValue = useMemo(() => ({ fireAnalyticsEvent }), [fireAnalyticsEvent]);

		if (!contentId) {
			return null;
		}

		// check if page owner exists and content is not a blog
		// checking against the name here for the case of anonymous users
		const shouldUsePageOwner = ownerName && contentType === 'page';

		const preloadOptions = Boolean(SPACE_OVERVIEW.match(contentUrl))
			? PreloadingPresets.onlyJSAndCriticalData
			: PreloadingPresets.onlyJS;

		return (
			<FeedCardContext.Provider value={feedCardContextValue}>
				<LivePagePreloader isLivePage={isLivePage}>
					<HoverPreloader
						name="popular-feed-card"
						preloadUrl={contentUrl}
						preloadOptions={preloadOptions}
					>
						<Wrapper
							data-testid="popular-feed-card"
							isActionMenuOpen={isActionMenuOpen}
							disableAnimation={isMounted || disableAnimation}
							onMouseEnter={handleMouseEnter}
							onFocus={handleMouseEnter}
							data-vc="popular-feed-card"
						>
							<FeedCardInvisibleFullSizedLink
								href={contentUrl}
								popularFeedTimeGranularity={timeGranularity}
								hasAiSnippet={!!hasAiSnippet}
							/>
							<Box xcss={avatarWrapperStyles}>
								<ProfileAvatar
									userId={shouldUsePageOwner ? ownerId : authorId}
									src={shouldUsePageOwner ? ownerSrc : authorSrc}
									canViewProfile
									size="medium"
								/>
							</Box>
							<FeedCardBody
								emoji={emoji}
								contentId={contentId}
								contentTitle={contentTitle}
								contentType={contentType}
								contentSubType={contentSubType}
								contentUrl={contentUrl}
								authorName={authorName}
								authorId={authorId}
								ownerName={ownerName}
								ownerId={ownerId}
								spaceId={spaceId}
								spaceName={spaceName}
								spaceUrl={spaceUrl}
								contentExcerpt={summaryContent}
								numComments={numComments}
								popularCardLayout
								timestamp={friendlyWhen}
								source="popular-feed"
								popularFeedTimeGranularity={timeGranularity}
								hasAiSnippet={!!hasAiSnippet}
							/>
							<div ref={bottomOfCardRef} />
						</Wrapper>
					</HoverPreloader>
				</LivePagePreloader>
			</FeedCardContext.Provider>
		);
	},
);
